
import Button from "../../../../../../../../shared/lib/html5-software-library/src/ranj/components/buttons/Button";
import Content from "../../../../../../../../shared/lib/html5-software-library/src/ranj/eng/content/Content";
import ContentTypes from "../../../../../../../../shared/lib/html5-software-library/src/ranj/eng/content/ContentTypes";
import KeyMomentChoiceContent from "../../../../../../../../shared/lib/html5-software-library/src/ranj/eng/content/KeyMomentChoiceContent";
import AppModel from "../../../../../../../../shared/src/ranj/app/core/AppModel";
import NGTDialogPlayerEvent from "../../NGTDialogPlayerEvent";
import { AbstractDialogPlayerOptionsMediator } from "../AbstractDialogPlayerOptionsMediator";
import Array from "../../../../../../../../shared/lib/html5-software-library/src/ranj/util/Array";
import LanguagePack from "../../../../../../../../shared/lib/html5-software-library/src/ranj/language/LanguagePack";

export default class MultipleSelectionOptionsMediator extends AbstractDialogPlayerOptionsMediator {
    /**
     * 
     * @param {*} target 
     * @param {*} dispatcher 
     * @param {*} soundManager 
     * @param {*} layoutManager 
     * @param {LanguagePack} languagePack 
     * @param {*} appModel 
     * @param {variableManager} variableManager 
     */
    constructor(target, dispatcher, soundManager, layoutManager, languagePack, appModel, variableManager) {
        super(target, dispatcher, languagePack, layoutManager, soundManager, appModel);

        this._variableManager = variableManager;
        this._speedFactor = AppModel.speedFactor;

        this._submitOption;
        this._optionButtonDict = {};
        /** @type {Button[]} */
        this._buttonArray = [];
        /** @type {Array<KeyMomentChoiceContent>} */
        this._currentOptionContents = [];

        this._onDialogContentBound = this._onDialogContent.bind(this);
    }

    initialize() {
        this._target.gotoAndStop(0);
        this._target.alpha = 0;

        for (let i = 1; i <= 9; i++) {
            const buttonSkin = this._target[`MSOptionButton${i}`];
            if (!buttonSkin) {
                break;
            }
            this._initForAnimation(buttonSkin);
            const button = this.createButton(buttonSkin, this._onOptionButtonClicked.bind(this));
            this._buttonArray.push(button);
        }

        this._initForAnimation(this._target.submitButton);
        this._submitButton = this.createButton(this._target.submitButton,
            this._onSubmitButtonClicked.bind(this),
            this.getText("dialog.multipleSelection.submitButton"),
            null,
            true);

        this._clearOptionButtons();
    }

    destroy() {
        this._optionButtonDict = null;
        this._clearProcessDialogOptionsTimeout();
        super.destroy();
    }

    // ------------------------------------------
    // 			Dialog displaying
    // ------------------------------------------
    /**
     * 
     * @param {Content} content 
     * @returns 
     */
    _onDialogContent({ params }) {
        const content = params?.data?.data?.content;
        if (!content) {
            return;
        }

        switch (content.getType()) {
            case ContentTypes.TYPE_KEY_MOMENT_CHOICE:
                this._currentOptionContents.push(content);
                this.getDialogPlayer().contentCompleted(content);
                break;
        }
    }

    _onSubmitButtonClicked() {
        // get all selected Content
        /** @type {KeyMomentChoiceContent[]} */
        const selectedContents = this._buttonArray
            .filter(button => button.getSelected())
            .map(button => this._optionButtonDict[button.skin.id]);

        for (const content of selectedContents) {
            if (ranj.DEBUG) {
                console.log(`[MultipleSelectionOptionsMediator] Selected content: ${content.getChoice()}`, content);
            }

            /** @type {VariableMutationNumberContent[]} */
            const varMutations = content.getScoreVariables();
            for (const mutation of varMutations) {
                this._variableManager.setVariable(mutation.getVariableName(), mutation.getValue(), mutation.getMutationType());
            }
        }

        let delay = 0;
        const delayInterval = 150 / this._speedFactor;
        this._animateOptionButtonOut(this._submitButton.skin, delay += delayInterval);
        for (let i = this._buttonArray.length - 1; i >= 0; i--, delay += delayInterval) {
            const button = this._buttonArray[i];
            button.setEnabled(false);
            this._animateOptionButtonOut(button.skin, delay);
        }

        createjs.Tween.get(this._target, { ovrride: true })
            .wait(delay += delayInterval)
            .to({ alpha: 0 }, 250 / this._speedFactor, createjs.Ease.quadOut);

        // Tell delegate that this option was chosen.
        setTimeout(() => {
            this._currentOptionContents = [];
            const dialogPlayer = this.getDialogPlayer();
            dialogPlayer.multipleOptionsChosen(this._submitOption, selectedContents);
            this._close();
        }, delay + 250 / this._speedFactor);
    }

    /**
     * 
     * @param {{ currentTarget: Button }} param0 
     */
    _onOptionButtonClicked({ currentTarget }) {
        const isSelected = !currentTarget.getSelected();
        currentTarget.setSelected(isSelected);
        const isSubmitEnabled = this._buttonArray.reduce((result, value) => result || value.getSelected(), false);
        this._submitButton.setEnabled(isSubmitEnabled);
    }

    _clearOptionButtons() {
        this._buttonArray.forEach(button => {
            button.removeEventListener("click", this._handleOptionButtonClickBound);
            button.skin.visible = false;
            delete this._optionButtonDict[button.skin.id];
        });
        this._submitButton.setVisible(false);
    }

    _handleDialogOptionsChanged() {
        this._processDialogOptions();
    }

    _processDialogOptions() {
        // Clear & hide all dialog buttons
        this._clearOptionButtons();

        const dialogOptions = this.getDialogPlayer().getCurrentOptions();

        this._submitButton.setEnabled(false);

        if (dialogOptions.length <= 0) {
            return;
        }

        createjs.Tween.get(this._target, { ovrride: true })
            .to({ alpha: 1 }, 500 / this._speedFactor, createjs.Ease.quadOut);

        // This interaction requires a Dialog Option to be used as the submit path
        this._submitOption = dialogOptions[0];

        const currentOptions = this._currentOptionContents.shuffled();
        let delay = 0;
        for (let i = 0; i < currentOptions.length; i++, delay += 250) {
            const content = currentOptions[i];
            const button = this._buttonArray[i];
            const label = content.getChoice() ?? "<TEXT NOT FOUND>";

            this._optionButtonDict[button.skin.id] = content;

            button.setLabel(label);
            button.setEnabled(true);

            this._animateOptionButtonIn(button.skin, delay);
        }

        this._animateOptionButtonIn(this._submitButton.skin, delay);
    }

    _initForAnimation(target) {
        target.yOrig = target.y;
        target.scaleOrig = target.scaleY;
    }

    _animateOptionButtonIn(target, delay) {
        target.visible = true;
        target.alpha = 0;
        target.scaleX = target.scaleY = .1 * target.scaleOrig;
        createjs.Tween.get(target)
            .wait(delay / this._speedFactor)
            .to({ alpha: 1, scaleX: target.scaleOrig, scaleY: target.scaleOrig }, 250 / this._speedFactor, createjs.Ease.backOut);
    }

    _animateOptionButtonOut(target, delay) {
        const scale = .1 * target.scaleOrig;
        createjs.Tween.get(target)
            .wait(delay / this._speedFactor)
            .to({ alpha: 0, scaleX: scale, scaleY: scale }, 250 / this._speedFactor, createjs.Ease.quadOut)
            .to({ visible: false }, 0);
    }

    _addDialogPlayerListeners() {
        super._addDialogPlayerListeners();

        if (!this._dialogPlayer) return;

        this._dialogPlayer.eventDispatcher.addEventListener(NGTDialogPlayerEvent.DIALOG_CONTENT, this._onDialogContentBound);
    }

    _removeDialogPlayerListeners() {
        super._removeDialogPlayerListeners();

        if (!this._dialogPlayer) return;

        this._dialogPlayer.eventDispatcher.removeEventListener(NGTDialogPlayerEvent.DIALOG_CONTENT, this._onDialogContentBound);
    }
}


